import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Check, Users, Gift, TrendingUp, Award, Share2, Loader2, Crown,
} from "lucide-react";
import api from "@/lib/api";
import { copyText, isNative } from "@/lib/mobile";
import { Progress } from "@/components/ui/progress";
import GameRates from "@/components/GameRates";

const TIER_COLORS = {
  Rookie: "#94A3B8",
  Bronze: "#CD7F32",
  Silver: "#CBD5E1",
  Gold: "#F5B301",
  Platinum: "#7DD3FC",
  Diamond: "#00E676",
};

export default function Referral() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/referral");
    setData(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14]">
        <Loader2 className="animate-spin text-[#00E676]" />
      </div>
    );
  }

  const webOrigin = process.env.REACT_APP_PUBLIC_WEB_URL || (isNative ? "" : window.location.origin);
  const link = webOrigin ? `${webOrigin.replace(/\/$/, "")}/login?ref=${data.referral_code}` : data.referral_code;
  const copy = async (text, label) => {
    try { await copyText(text); }
    catch { toast.error("Unable to copy"); return; }
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "ProCrypto", text: `Join ProCrypto with my code ${data.referral_code} and get 7 USDT!`, ...(webOrigin ? { url: link } : {}) });
      } catch {}
    } else {
      copy(link, "Invite link");
    }
  };

  const tierColor = TIER_COLORS[data.tier] || "#00E676";
  const nt = data.next_tier;

  return (
    <div className="pb-28 grain min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0E14]/80 border-b border-[#1E293B]">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="referral-back-btn"
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-[#121721] border border-[#1E293B] flex items-center justify-center"
          >
            <ArrowLeft size={18} />
          </button>
          <span className="font-display font-bold text-lg">Referral & Rewards</span>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 pt-5 space-y-4">
        {/* tier card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl border border-[#1E293B] bg-[#121721] p-5 overflow-hidden"
        >
          <div
            className="absolute -right-8 -top-8 w-36 h-36 rounded-full blur-2xl opacity-20"
            style={{ background: tierColor }}
          />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">Your Tier</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Crown size={22} style={{ color: tierColor }} />
                <span data-testid="referral-tier" className="font-display text-2xl font-bold" style={{ color: tierColor }}>
                  {data.tier}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Commission</p>
              <p className="font-mono-num text-2xl font-bold text-[#00E676]">
                {(data.commission_rate * 100).toFixed(2)}%
              </p>
            </div>
          </div>
          <div className="flex gap-4 mt-4 text-xs">
            <div>
              <span className="text-slate-500">Referrals</span>
              <div data-testid="referral-count" className="font-mono-num text-slate-100 text-base">
                {data.referral_count}
              </div>
            </div>
            <div>
              <span className="text-slate-500">Withdraw boost</span>
              <div className="font-mono-num text-slate-100 text-base">×{data.withdraw_multiplier}</div>
            </div>
          </div>

          {nt && (
            <div className="mt-4 pt-4 border-t border-[#1E293B]">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-slate-400">
                  {nt.needed} more to reach <span style={{ color: TIER_COLORS[nt.name] }}>{nt.name}</span> ({(nt.rate * 100).toFixed(2)}%)
                </span>
              </div>
              <Progress
                value={nt.needed === 0 ? 100 : (data.referral_count / (data.referral_count + nt.needed)) * 100}
                className="h-2 bg-[#1E2330]"
              />
            </div>
          )}
        </motion.div>

        {/* code + share */}
        <div className="rounded-2xl border border-[#1E293B] bg-[#121721] p-5">
          <p className="text-xs text-slate-400 mb-2">Your referral code</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl border border-dashed border-[#00E676]/40 bg-[#0E2E25] px-4 py-3">
              <span data-testid="referral-code" className="font-mono-num text-xl font-bold text-[#00E676] tracking-[0.3em]">
                {data.referral_code}
              </span>
            </div>
            <button
              data-testid="copy-referral-code-btn"
              onClick={() => copy(data.referral_code, "Code")}
              className="w-12 h-12 rounded-xl bg-[#1E2330] border border-[#1E293B] flex items-center justify-center"
            >
              {copied ? <Check size={18} className="text-[#00E676]" /> : <Copy size={18} className="text-slate-300" />}
            </button>
          </div>
          <button
            data-testid="share-referral-btn"
            onClick={share}
            className="mt-3 w-full h-11 rounded-xl bg-[#00E676] hover:bg-[#00C853] text-black font-bold flex items-center justify-center gap-2 transition-colors"
          >
            <Share2 size={16} /> Share invite link
          </button>
        </div>

        {/* how it works */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-[#1E293B] bg-[#121721] p-4">
            <Gift className="text-[#00E676] mb-2" size={20} />
            <div className="font-mono-num text-lg font-bold">+{data.new_user_bonus} USDT</div>
            <div className="text-[11px] text-slate-500">Your friend gets on signup</div>
          </div>
          <div className="rounded-xl border border-[#1E293B] bg-[#121721] p-4">
            <Award className="text-[#00E676] mb-2" size={20} />
            <div className="font-mono-num text-lg font-bold">+{data.referrer_reward} USDT</div>
            <div className="text-[11px] text-slate-500">You get per referral</div>
          </div>
        </div>

        {/* earnings */}
        <div className="rounded-2xl border border-[#1E293B] bg-[#121721] p-5 flex items-center gap-4">
          <TrendingUp className="text-[#00E676]" size={22} />
          <div className="flex-1">
            <div className="text-xs text-slate-400">Chain commission earned</div>
            <div data-testid="total-commission" className="font-mono-num text-xl font-bold text-[#00E676]">
              +{data.total_commission.toFixed(4)} USDT
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400">Signup rewards</div>
            <div className="font-mono-num text-base font-semibold">+{data.total_referral_reward.toFixed(2)}</div>
          </div>
        </div>

        {/* tier table */}
        <GameRates />
        <div className="rounded-2xl border border-[#1E293B] bg-[#121721] overflow-hidden">
          <div className="px-4 pt-4 pb-2 text-sm font-semibold">Tier ladder</div>
          <div className="text-[11px]">
            <div className="grid grid-cols-4 px-4 py-2 text-slate-500 border-b border-[#1E293B]/60">
              <span>Tier</span>
              <span>Referrals</span>
              <span className="text-right">Commission</span>
              <span className="text-right">Withdraw</span>
            </div>
            {data.tiers.map((t) => {
              const active = t.name === data.tier;
              return (
                <div
                  key={t.name}
                  className={`grid grid-cols-4 px-4 py-2.5 border-b border-[#1E293B]/40 ${
                    active ? "bg-[#0E2E25]" : ""
                  }`}
                >
                  <span className="font-semibold" style={{ color: TIER_COLORS[t.name] }}>
                    {t.name}
                  </span>
                  <span className="font-mono-num text-slate-300">
                    {t.max === null ? `${t.min}+` : `${t.min}-${t.max}`}
                  </span>
                  <span className="font-mono-num text-right text-[#00E676]">{(t.rate * 100).toFixed(2)}%</span>
                  <span className="font-mono-num text-right text-slate-300">×{t.mult}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* referrals list */}
        <div className="rounded-2xl border border-[#1E293B] bg-[#121721] overflow-hidden">
          <div className="px-4 pt-4 pb-2 text-sm font-semibold flex items-center gap-2">
            <Users size={16} /> Your referrals ({data.referrals.length})
          </div>
          {data.referrals.length === 0 ? (
            <p className="text-xs text-slate-500 px-4 pb-4">
              No referrals yet. Share your code to start earning.
            </p>
          ) : (
            data.referrals.map((r, i) => (
              <div
                key={i}
                data-testid={`referral-item-${i}`}
                className="flex items-center gap-3 px-4 py-3 border-t border-[#1E293B]/60"
              >
                <div className="w-8 h-8 rounded-full bg-[#1E2330] flex items-center justify-center text-xs font-bold text-slate-300">
                  {(r.name || r.email)[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{r.name || r.email}</div>
                  <div className="text-[10px] text-slate-500 font-mono-num truncate">{r.email}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[10px] text-slate-500">Deposited</div>
                  <div className="font-mono-num text-xs text-slate-300">{r.deposited} USDT</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
